import { Tab, TabList, Tabs } from '@react-spectrum/s2';
import Add from '@react-spectrum/s2/icons/Add';
import { iconStyle } from '@react-spectrum/s2/style' with { type: 'macro' };
import type { ReactNode } from 'react';
import type { Key } from 'react-aria-components';

import { useBridgeApi } from '@/api/BridgeApi.ts';

type PeopleTab = 'contacts' | 'audiences';

interface PeopleTabsHeaderProps {
	activeTab: PeopleTab;
	onCreate?: () => void;
	createLabel?: string;
	actions?: ReactNode;
}

const TABS: { id: PeopleTab; label: string }[] = [
	{ id: 'contacts', label: 'Contacts' },
	{ id: 'audiences', label: 'Audiences' },
];

export const PeopleTabsHeader = ({
	activeTab,
	onCreate,
	createLabel,
	actions,
}: PeopleTabsHeaderProps) => {
	const bridgeApi = useBridgeApi();

	const handleSelectionChange = (key: Key) => {
		const tab = key as PeopleTab;
		if (tab === activeTab) return;
		bridgeApi.sendAction({ type: 'do_navigate_to_space', space_type: tab });
	};

	return (
		<div
			className="flex shrink-0 flex-col px-6 pt-4 pb-10"
			data-testid="people-tabs-header"
		>
			<div className="flex items-center justify-between pb-6">
				<h1 className="text-xl font-bold text-(--text-primary)">People</h1>
				<div className="flex items-center gap-2">
					{actions}
					{onCreate && createLabel && (
						<button
							type="button"
							onClick={onCreate}
							aria-label={createLabel}
							className="flex h-8 items-center gap-1 rounded-md px-2
								text-[var(--text-primary)] transition-colors
								hover:bg-[var(--glass-bg-hover)]"
						>
							<Add styles={iconStyle({ size: 'M' })} />
							<span className="text-sm font-medium">{createLabel}</span>
						</button>
					)}
				</div>
			</div>
			<Tabs
				selectedKey={activeTab}
				onSelectionChange={handleSelectionChange}
				aria-label="People"
			>
				<TabList>
					{TABS.map((tab) => (
						<Tab key={tab.id} id={tab.id}>
							{tab.label}
						</Tab>
					))}
				</TabList>
			</Tabs>
		</div>
	);
};
